import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {map, Observable, throwError} from 'rxjs';

import {IStringAnyMap} from '../types';
import {BooxStorageService} from './boox-storage.service';
import { BOOX_APIS, BOOX_DEFAULT_API, BOOX_SESSION, BooxApisToken, BooxApiToken, BooxSessionToken } from '../token';

export interface IBooxRequestOptions {
  boox?: string; // if not set it will use default boox instance configured in environment
  headers?: IStringAnyMap; // additional headers
  apiKey?: boolean; // use API-Key default true
  auth?: boolean; // use Session-Key default true of sign in
  params?: IStringAnyMap; // query parameter
  responseType?: string; //
  extractResource?: boolean; // extract resource from response
}

@Injectable({
  providedIn: 'root',
})
export class BooxApiService {

  token: string | null = '';

  constructor(
    private http: HttpClient,
    private store: BooxStorageService,
    @Inject(BOOX_APIS) private apis: BooxApisToken,
    @Inject(BOOX_DEFAULT_API) private default_api: string,
    @Inject(BOOX_SESSION) private sessionSetting: BooxSessionToken,
  ) {}

  /**
   * Get the boox config
   * @param _booxName Boox config name
   * @returns environment boox config
   */
  private _booxConfig(_booxName: string): BooxApiToken {
    const booxName = _booxName as keyof typeof this.apis;
    if (!this.apis[booxName]) {
      throw new Error(`API instance ${booxName} not found`);
    }
    return this.apis[booxName];
  }

  /**
   * Ensures correct URL formatting
   *
   * @param host host url
   * @param rootPath root path
   * @param path path to be appended
   * @returns URL
   */
  private _buildUrl(host: string, rootPath: string, path: string): URL {

    rootPath = !rootPath?.startsWith('/') ? '/' + rootPath : rootPath;
    rootPath = rootPath?.endsWith('/') ? rootPath.slice(0, -1) : rootPath;

    path = !path?.startsWith('/') ? '/' + (path || '') : path;
    const url = new URL(`${rootPath}${path}`, `${host}`);

    return url;
  }

  private _buildRequestOptions(options: IBooxRequestOptions, path: string): any {

    const booxName = options.boox || this.default_api;
    const boox = this._booxConfig(booxName);

    let headers = new HttpHeaders({
      ...(options.headers || {}),
      ...(options.apiKey !== false && boox.apiKey ? {[boox.headerApiKey]: boox.apiKey} : {})
    });

    const authToken = this.token || this.store.getItem(this.sessionSetting.storage_key);

    if (!!authToken && options.auth !== false) {
      headers = headers.set(boox.headerSessionToken, authToken);
    } else if (options.auth === true) {
      return throwError(() => new Error('Authentication is required but there is no session'));
    }

    const url = this._buildUrl(boox.host, boox.rootPath, path).toString();
    const requestOptions: IStringAnyMap = {
      headers,
      params: new HttpParams({fromObject: options.params || {}}),
      ...(options.responseType ? {responseType: options.responseType} : {})
    };

    return {url, requestOptions};
  }

  setToken(_token: string | null): void {
    this.token = _token;
  }

  /**
   * Send GET request to Boox instance
   *
   * @param _path {string} Request path
   * @param _options {IBooxRequestOptions} Request options
   * @returns {Observable<T>}
   */
  get<T = IStringAnyMap>(_path: string, _options: IBooxRequestOptions = {}): Observable<T> {

    const {url, requestOptions} = this._buildRequestOptions(_options, _path);

    // extract resource from response if extractResource is true
    return this.http.get<T>(url, requestOptions).pipe(
      map((_response: any) => (_options.extractResource ? _response['resource'] : _response) as T)
    );

  }

  /**
   * Send POST request to Boox instance
   *
   * @param _path {string} Request path
   * @param _data {any} Request data
   * @param _options {IBooxRequestOptions} Request options
   * @returns {Observable<T>}
   */
  post<T = IStringAnyMap>(_path: string, _data: any, _options: IBooxRequestOptions = {}): Observable<T> {

    const {url, requestOptions} = this._buildRequestOptions(_options, _path);

    return this.http.post<T>(url, _data, requestOptions).pipe(
      map((_response: any) => (_options.extractResource ? _response['resource'] : _response) as T)
    );
  }

  /**
   * Send PATCH request to Boox instance
   *
   * @param _path {string} Request path
   * @param _data {any} Request data
   * @param _options {IBooxRequestOptions} Request options
   * @returns {Observable<T>}
   */
  patch<T = IStringAnyMap>(_path: string, _data: any, _options: IBooxRequestOptions = {}): Observable<T> {

    const {url, requestOptions} = this._buildRequestOptions(_options, _path);

    return this.http.patch<T>(url, _data, requestOptions).pipe(
      map((_response: any) => (_options.extractResource ? _response['resource'] : _response) as T)
    );
  }

  /**
   * Send PUT request to Boox instance
   *
   * @param _path {string} Request path
   * @param _data {any} Request data
   * @param _options {IBooxRequestOptions} Request options
   * @returns {Observable<T>}
   */
  put<T = IStringAnyMap>(_path: string, _data: any, _options: IBooxRequestOptions = {}): Observable<T> {

    const {url, requestOptions} = this._buildRequestOptions(_options, _path);

    return this.http.put<T>(url, _data, requestOptions).pipe(
      map((_response: any) => (_options.extractResource ? _response['resource'] : _response) as T)
    );
  }

  /**
   * Send DELETE request to Boox instance
   *
   * @param _path {string} Request path
   * @param _options {IBooxRequestOptions} Request options
   * @returns {Observable<T>}
   */
  delete<T = IStringAnyMap>(_path: string, _options: IBooxRequestOptions = {}): Observable<T> {

    const {url, requestOptions} = this._buildRequestOptions(_options, _path);

    return this.http.delete<T>(url, requestOptions).pipe(
      map((_response: any) => (_options.extractResource ? _response['resource'] : _response) as T)
    );
  }

}
